import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Cloud, 
  CircuitBoard, 
  Compass,
  Calendar,
  MapPin,
  Globe,
  Armchair,
  Network,
  ArrowRight,
  Home
} from 'lucide-react';
import SEO from './SEO';
import { EditableText, EditableImage, EditableSection, useAdmin } from './AdminContentEditor';

// HTHF Logo
const HTHF_LOGO = 'https://customer-assets.emergentagent.com/job_74632013-6c46-47ca-b50d-529d9afc8cf5/artifacts/j401uj5d_2.png';

// Animated Dot Grid Background
const DotGridBackground = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <svg className="w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="dotGrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="currentColor" className="text-black" />
          </pattern>
        </defs>
        <motion.rect 
          width="100%" 
          height="100%" 
          fill="url(#dotGrid)"
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
      </svg>
    </div>
  );
};

// Section wrapper with fade-in animation
const Section = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {children}
    </motion.section>
  );
};

// Thin line divider
const Divider = () => (
  <div className="w-full h-px bg-black/10 my-24 md:my-32" />
);

// Icon wrapper with hover effect
const IconWrapper = ({ children, className = "" }) => (
  <motion.div 
    className={`inline-flex items-center justify-center ${className}`}
    whileHover={{ scale: 1.05 }}
    transition={{ duration: 0.2 }}
  >
    {children}
  </motion.div>
);

// Section header with hover micro-interaction
const SectionHeader = ({ children, className = "" }) => (
  <motion.h2 
    className={`text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-black ${className}`}
    whileHover={{ x: 4 }}
    transition={{ duration: 0.2 }}
  >
    {children}
  </motion.h2>
);

export const EmergentPage = () => {
  const { isEditMode } = useAdmin();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-black relative">
      <SEO 
        title="Emergent x Data Dreaming | Partnership"
        description="A Founder Speed Community Partnership"
        url="/emergent"
      />
      
      <DotGridBackground />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="p-2 -ml-2 rounded-lg hover:bg-black/5 transition-colors">
            <Home className="w-5 h-5 text-black" strokeWidth={1.5} />
          </Link>
          <span className="text-xs tracking-[0.2em] uppercase text-black/50">Partnership Concept</span>
        </div>
      </nav>

      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-32 pb-24">
        
        {/* HERO */}
        <EditableSection sectionId="emergent-hero">
          <Section className="min-h-[60vh] flex flex-col justify-center items-center text-center py-24">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                <EditableText
                  sectionId="emergent-hero"
                  field="title"
                  defaultValue="Emergent x Data Dreaming"
                  className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-black"
                />
              </h1>
              <p className="text-lg md:text-xl text-black/60 tracking-wide">
                <EditableText
                  sectionId="emergent-hero"
                  field="subtitle"
                  defaultValue="AI Community Partnership Concept"
                  className="text-lg md:text-xl text-black/60 tracking-wide"
                />
              </p>
            </motion.div>
          </Section>
        </EditableSection>

        <Divider />

        {/* BUILT AT FOUNDER SPEED */}
        <EditableSection sectionId="emergent-speed">
          <Section id="founder-speed" className="py-16">
            <div className="grid md:grid-cols-12 gap-8 md:gap-16">
              <div className="md:col-span-4">
                <IconWrapper className="mb-6">
                  <Zap className="w-8 h-8 text-black" strokeWidth={1} />
                </IconWrapper>
                <SectionHeader>
                  <EditableText
                    sectionId="emergent-speed"
                    field="title"
                    defaultValue="Built at Founder Speed"
                    className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-black"
                  />
                </SectionHeader>
              </div>
              <div className="md:col-span-8 space-y-6">
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-speed"
                    field="paragraph1"
                    defaultValue="Phygital is a preference infrastructure platform scaling in stealth."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-speed"
                    field="paragraph2"
                    defaultValue="It was built using Emergent to move at founder speed."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-speed"
                    field="paragraph3"
                    defaultValue="Publicly, the ecosystem layer is Data Dreaming."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
              </div>
            </div>
          </Section>
        </EditableSection>

        <Divider />

        {/* WHAT IS DATA DREAMING */}
        <EditableSection sectionId="emergent-datadreaming">
          <Section id="data-dreaming" className="py-16">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-12 mb-8">
                <IconWrapper>
                  <Cloud className="w-6 h-6 text-black" strokeWidth={1} />
                </IconWrapper>
                <IconWrapper>
                  <CircuitBoard className="w-6 h-6 text-black" strokeWidth={1} />
                </IconWrapper>
                <IconWrapper>
                  <Compass className="w-6 h-6 text-black" strokeWidth={1} />
                </IconWrapper>
              </div>
              <SectionHeader className="mb-8">
                <EditableText
                  sectionId="emergent-datadreaming"
                  field="title"
                  defaultValue="What Is Data Dreaming"
                  className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-black"
                />
              </SectionHeader>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-8">
              <p className="text-lg md:text-xl text-black/70 leading-relaxed text-center">
                <EditableText
                  sectionId="emergent-datadreaming"
                  field="intro"
                  defaultValue="Data Dreaming is a curated community for creative and analytical builders."
                  className="text-lg md:text-xl text-black/70 leading-relaxed"
                />
              </p>
              
              <p className="text-lg md:text-xl text-black/70 leading-relaxed text-center">
                <EditableText
                  sectionId="emergent-datadreaming"
                  field="intersection"
                  defaultValue="It operates at the intersection of:"
                  className="text-lg md:text-xl text-black/70 leading-relaxed"
                />
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-8">
                {['Technology', 'Fashion', 'Wellness', 'Music', 'Media', 'Film', 'AI', 'Real world experiences'].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="text-center py-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: i * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <span className="text-sm md:text-base font-medium text-black/80 tracking-wide">
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>
              
              <p className="text-lg md:text-xl text-black/70 leading-relaxed text-center">
                <EditableText
                  sectionId="emergent-datadreaming"
                  field="activations"
                  defaultValue="It produces recurring IRL and URL activations, workshops, and education formats for high agency builders."
                  className="text-lg md:text-xl text-black/70 leading-relaxed"
                  multiline={true}
                />
              </p>
              
              <p className="text-xl md:text-2xl text-black font-medium text-center italic">
                <EditableText
                  sectionId="emergent-datadreaming"
                  field="tagline"
                  defaultValue="For the people whose heads are in the clouds and still ship."
                  className="text-xl md:text-2xl text-black font-medium italic"
                />
              </p>
            </div>
          </Section>
        </EditableSection>

        <Divider />

        {/* 2026 ACTIVATION MODEL */}
        <EditableSection sectionId="emergent-activation">
          <Section id="activation-model" className="py-16">
            <div className="grid md:grid-cols-12 gap-8 md:gap-16">
              <div className="md:col-span-4">
                <div className="flex items-center gap-6 mb-6">
                  <IconWrapper>
                    <Calendar className="w-6 h-6 text-black" strokeWidth={1} />
                  </IconWrapper>
                  <IconWrapper>
                    <MapPin className="w-6 h-6 text-black" strokeWidth={1} />
                  </IconWrapper>
                  <IconWrapper>
                    <Globe className="w-6 h-6 text-black" strokeWidth={1} />
                  </IconWrapper>
                </div>
                <SectionHeader>
                  <EditableText
                    sectionId="emergent-activation"
                    field="title"
                    defaultValue="2026 Activation Model"
                    className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-black"
                  />
                </SectionHeader>
              </div>
              
              <div className="md:col-span-8 space-y-8">
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-activation"
                    field="intro"
                    defaultValue="Data Dreaming operates on a recurring multi-city activation model across key cultural markets."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
                
                <div>
                  <p className="text-lg text-black/70 leading-relaxed mb-4">
                    <EditableText
                      sectionId="emergent-activation"
                      field="calendar_label"
                      defaultValue="The 2026 calendar includes activations across:"
                      className="text-lg text-black/70 leading-relaxed"
                    />
                  </p>
                  
                  <div className="flex flex-wrap gap-4">
                    {['Los Angeles', 'London', 'New York'].map((city, i) => (
                      <span key={i} className="text-lg font-medium text-black/80">{city}</span>
                    ))}
                  </div>
                </div>
                
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-activation"
                    field="anchored"
                    defaultValue="Anchored around high signal cultural moments including fashion weeks, swim weeks, wellness gatherings, and curated AI culture events."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                    multiline={true}
                  />
                </p>
                
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-activation"
                    field="purpose"
                    defaultValue="Each activation is designed to gather founders, creative technologists, artists, and operators building at the intersection of AI and culture."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                    multiline={true}
                  />
                </p>
              </div>
            </div>
          </Section>
        </EditableSection>

        <Divider />

        {/* SPRING LAUNCH */}
        <EditableSection sectionId="emergent-spring">
          <Section id="spring-launch" className="py-16">
            <div className="max-w-3xl mx-auto">
              <p className="text-sm tracking-[0.2em] uppercase text-black/50 mb-6 text-center">
                <EditableText
                  sectionId="emergent-spring"
                  field="label"
                  defaultValue="Spring Launch"
                  className="text-sm tracking-[0.2em] uppercase text-black/50"
                />
              </p>
              
              <div className="bg-black p-8 md:p-12 mb-8">
                <EditableImage
                  sectionId="emergent-spring"
                  field="hthf_logo"
                  defaultSrc={HTHF_LOGO}
                  alt="High Tech High Fashion"
                  className="h-24 md:h-32 mx-auto object-contain"
                />
              </div>
              
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold mb-2">
                  <EditableText
                    sectionId="emergent-spring"
                    field="event_title"
                    defaultValue="High Tech High Fashion"
                    className="text-2xl md:text-3xl font-bold text-black"
                  />
                </h3>
                <p className="text-black/60">
                  <EditableText
                    sectionId="emergent-spring"
                    field="event_date"
                    defaultValue="March 11"
                    className="text-black/60"
                  />
                </p>
                <p className="text-black/60">
                  <EditableText
                    sectionId="emergent-spring"
                    field="event_location"
                    defaultValue="Los Angeles"
                    className="text-black/60"
                  />
                </p>
              </div>
              
              <p className="text-lg text-black/70 leading-relaxed text-center mb-6">
                <EditableText
                  sectionId="emergent-spring"
                  field="description"
                  defaultValue="A curated mixer and panel during Spring LA Fashion Week bringing together:"
                  className="text-lg text-black/70 leading-relaxed"
                />
              </p>
              
              <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
                {['AI artists', 'Creative technologists', 'Founders', 'Fashion and media operators'].map((item, i) => (
                  <span key={i} className="text-sm text-black/60">{item}</span>
                ))}
              </div>
            </div>
          </Section>
        </EditableSection>

        <Divider />

        {/* FRONT ROW */}
        <EditableSection sectionId="emergent-frontrow">
          <Section id="front-row" className="py-16">
            <div className="grid md:grid-cols-12 gap-8 md:gap-16">
              <div className="md:col-span-4">
                <IconWrapper className="mb-6">
                  <Armchair className="w-8 h-8 text-black" strokeWidth={1} />
                </IconWrapper>
                <SectionHeader>
                  <EditableText
                    sectionId="emergent-frontrow"
                    field="title"
                    defaultValue="Front Row"
                    className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-black"
                  />
                </SectionHeader>
              </div>
              <div className="md:col-span-8 space-y-6">
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-frontrow"
                    field="paragraph1"
                    defaultValue="Front Row is a live build education format."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-frontrow"
                    field="paragraph2"
                    defaultValue="Founders and students get a seat inside real company creation in real time."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-frontrow"
                    field="paragraph3"
                    defaultValue="Piloting with LMU and expanding into additional university ecosystems."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
                <p className="text-xl md:text-2xl text-black font-medium">
                  <EditableText
                    sectionId="emergent-frontrow"
                    field="tagline"
                    defaultValue="Front Row connects culture, education, and execution."
                    className="text-xl md:text-2xl text-black font-medium"
                  />
                </p>
              </div>
            </div>
          </Section>
        </EditableSection>

        <Divider />

        {/* WHY EMERGENT */}
        <EditableSection sectionId="emergent-why">
          <Section id="why-emergent" className="py-16">
            <div className="grid md:grid-cols-12 gap-8 md:gap-16">
              <div className="md:col-span-4">
                <IconWrapper className="mb-6">
                  <Network className="w-8 h-8 text-black" strokeWidth={1} />
                </IconWrapper>
                <SectionHeader>
                  <EditableText
                    sectionId="emergent-why"
                    field="title"
                    defaultValue="Why Emergent"
                    className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-black"
                  />
                </SectionHeader>
              </div>
              <div className="md:col-span-8 space-y-6">
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-why"
                    field="paragraph1"
                    defaultValue="Emergent enables founder speed building."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
                <p className="text-lg md:text-xl text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-why"
                    field="paragraph2"
                    defaultValue="Data Dreaming gathers the builders shaping AI across culture."
                    className="text-lg md:text-xl text-black/70 leading-relaxed"
                  />
                </p>
                <p className="text-xl md:text-2xl text-black font-medium">
                  <EditableText
                    sectionId="emergent-why"
                    field="position"
                    defaultValue="Position Emergent as the AI Community Partner behind Data Dreaming's 2026 activation calendar."
                    className="text-xl md:text-2xl text-black font-medium"
                  />
                </p>
              </div>
            </div>
          </Section>
        </EditableSection>

        <Divider />

        {/* PARTNERSHIP CONCEPT */}
        <EditableSection sectionId="emergent-partnership">
          <Section id="partnership" className="py-16">
            <SectionHeader className="text-center mb-16">
              <EditableText
                sectionId="emergent-partnership"
                field="title"
                defaultValue="Partnership Concept"
                className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-black"
              />
            </SectionHeader>
            
            <div className="grid md:grid-cols-3 gap-8 md:gap-4">
              {/* Phase 1 */}
              <div className="text-center md:text-left p-8 border-t md:border-t-0 md:border-l border-black/10 first:border-t-0 md:first:border-l-0">
                <p className="text-sm tracking-[0.2em] uppercase text-black/50 mb-4">
                  <EditableText
                    sectionId="emergent-partnership"
                    field="phase1_label"
                    defaultValue="Phase 1"
                    className="text-sm tracking-[0.2em] uppercase text-black/50"
                  />
                </p>
                <p className="text-lg text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-partnership"
                    field="phase1_desc"
                    defaultValue="Spring LA Fashion Week alignment."
                    className="text-lg text-black/70 leading-relaxed"
                  />
                </p>
              </div>
              
              {/* Phase 2 */}
              <div className="text-center md:text-left p-8 border-t md:border-t-0 md:border-l border-black/10">
                <p className="text-sm tracking-[0.2em] uppercase text-black/50 mb-4">
                  <EditableText
                    sectionId="emergent-partnership"
                    field="phase2_label"
                    defaultValue="Phase 2"
                    className="text-sm tracking-[0.2em] uppercase text-black/50"
                  />
                </p>
                <p className="text-lg text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-partnership"
                    field="phase2_desc"
                    defaultValue="Front Row education pilot and workshop collaboration."
                    className="text-lg text-black/70 leading-relaxed"
                  />
                </p>
              </div>
              
              {/* Phase 3 */}
              <div className="text-center md:text-left p-8 border-t md:border-t-0 md:border-l border-black/10">
                <p className="text-sm tracking-[0.2em] uppercase text-black/50 mb-4">
                  <EditableText
                    sectionId="emergent-partnership"
                    field="phase3_label"
                    defaultValue="Phase 3"
                    className="text-sm tracking-[0.2em] uppercase text-black/50"
                  />
                </p>
                <p className="text-lg text-black/70 leading-relaxed">
                  <EditableText
                    sectionId="emergent-partnership"
                    field="phase3_desc"
                    defaultValue="Extension across 2026 multi-city activations."
                    className="text-lg text-black/70 leading-relaxed"
                  />
                </p>
              </div>
            </div>
            
            <p className="text-xl md:text-2xl text-black font-medium text-center mt-16">
              <EditableText
                sectionId="emergent-partnership"
                field="closing"
                defaultValue="This partnership begins with culture and scales with proof."
                className="text-xl md:text-2xl text-black font-medium"
              />
            </p>
          </Section>
        </EditableSection>

        <Divider />

        {/* NEXT STEP */}
        <EditableSection sectionId="emergent-nextstep">
          <Section id="next-step" className="py-16 text-center">
            <IconWrapper className="mb-8">
              <ArrowRight className="w-10 h-10 text-black" strokeWidth={1} />
            </IconWrapper>
            
            <SectionHeader className="mb-8">
              <EditableText
                sectionId="emergent-nextstep"
                field="title"
                defaultValue="Next Step"
                className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-black"
              />
            </SectionHeader>
            
            <p className="text-xl md:text-2xl text-black/70 leading-relaxed max-w-2xl mx-auto">
              <EditableText
                sectionId="emergent-nextstep"
                field="cta"
                defaultValue="Explore Spring 2026 alignment and long term collaboration."
                className="text-xl md:text-2xl text-black/70 leading-relaxed"
              />
            </p>
          </Section>
        </EditableSection>

        {/* Footer */}
        <footer className="py-16 border-t border-black/10 text-center">
          <p className="text-sm text-black/40">
            <EditableText
              sectionId="emergent-footer"
              field="copyright"
              defaultValue="© 2026 Data Dreaming"
              className="text-sm text-black/40"
            />
          </p>
        </footer>
      </div>
    </div>
  );
};

export default EmergentPage;
